<template>
  <div class="qw-detail-list" v-cloak>
    <GridPanelHeader
      :title="detailTitle"
      :grid-ref="$refs[dtGridRefKey]"
      :show-add="false"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
    />
    <div v-if="isLoading" style="padding: 16px; text-align: center">
      <Loader :loading="isLoading" size="sm" />
    </div>
    <DxDataGrid
      v-else
      :ref="dtGridRefKey"
      :data-source="documents"
      :column-min-width="80"
      style="min-height: 200px; max-height: 400px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :row-alternation-enabled="true"
      :element-attr="gridAttributes"
    >
      <DxColumn
        dataField="document_type_label"
        :caption="$t('scale.quantity_warnings.document_type')"
        :calculateCellValue="getDocumentTypeLabel"
        :allowFiltering="false"
        :allowSorting="false"
      />
      <DxColumn
        dataField="document_number"
        :caption="$t('stock_management.document.document_number')"
        :allowFiltering="false"
        :allowSorting="false"
      />
      <DxColumn
        dataField="date"
        :caption="$t('common.date')"
        data-type="datetime"
        :format="dateTimeFormat"
        :allowFiltering="false"
        :allowSorting="false"
      />
      <DxColumn
        dataField="quantity"
        :caption="$t('common.quantity')"
        data-type="number"
        :allowFiltering="false"
        :allowSorting="false"
        value-format="#,##0.##"
      />
      <DxColumn
        dataField="measure_unit_name"
        :caption="$t('common.measure_unit')"
        :allowFiltering="false"
        :allowSorting="false"
      />
      <DxScrolling show-scrollbar="always" :use-native="false" />
      <DxHeaderFilter :visible="false" />
      <DxFilterRow :visible="false" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxHeaderFilter,
  DxFilterRow,
  DxScrolling,
} from "devextreme-vue/data-grid";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import Loader from "@/components/Loader";

export default {
  name: "QuantityWarningDetailList",
  components: {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxFilterRow,
    DxScrolling,
    GridPanelHeader,
    Loader,
  },
  props: {
    rowData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dtGridRefKey: "qwDetailGrid",
      gridAttributes: {
        class: "smagria-grid qw-detail-grid",
      },
      documents: [],
      isLoading: false,
      transactionTypesList: [],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      weighingOnScaleTypes: "scale/weighingOnScaleTypes",
    }),
    weighingTypesList() {
      const types = this.weighingOnScaleTypes;
      if (Array.isArray(types)) {
        return types.map((t) => ({ weighing_type_id: t.id, weighing_type: t.name }));
      }
      if (types && typeof types === "object") {
        return Object.keys(types).map((key) => ({ weighing_type_id: types[key].id, weighing_type: types[key].name }));
      }
      return [];
    },
    detailTitle() {
      const parts = [];
      if (this.rowData?.legal_entity_name) parts.push(this.rowData.legal_entity_name);
      if (this.rowData?.product_name) parts.push(this.rowData.product_name);
      if (this.rowData?.year) parts.push(this.rowData.year);
      return parts.join(" / ");
    },
    dateTimeFormat() {
      return this.getDxGridDefaultDateFormat;
    },
  },
  methods: {
    ...mapActions({
      getQuantityWarningDocuments: "scaleQuantityWarnings/getQuantityWarningDocuments",
      getQuantityWarningTransactionTypes: "scaleQuantityWarnings/getQuantityWarningTransactionTypes",
      getWeighingOnScaleTypes: "scale/getWeighingOnScaleTypes",
    }),
    getDocumentTypeLabel(rowData) {
      if (rowData.weighing_type_id) {
        const found = this.weighingTypesList.find((t) => t.weighing_type_id === rowData.weighing_type_id);
        if (found) return found.weighing_type;
      }
      if (rowData.transaction_type) {
        const found = this.transactionTypesList.find((t) => t.uuid === rowData.transaction_type);
        if (found) return found.name;
      }
      return rowData.document_type_label || "";
    },
    async loadDocuments() {
      if (!this.rowData?.uuid) return;
      this.isLoading = true;
      try {
        const response = await this.getQuantityWarningDocuments({
          uuid: this.rowData.uuid,
          limit: 1000,
        });
        const data = response?.data?.data || response?.data || [];
        this.documents = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("Error loading QW documents:", e);
        this.documents = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.getWeighingOnScaleTypes();
    this.getQuantityWarningTransactionTypes().then((response) => {
      const data = response?.data?.data || response?.data || [];
      this.transactionTypesList = Array.isArray(data) ? data : [];
    });
    this.loadDocuments();
  },
};
</script>

<style scoped>
.qw-detail-list {
  padding: 8px 16px 16px;
}
</style>
